import { z } from 'zod'
import { moneySchema, optionalText, pageQuerySchema, versionSchema } from './common.js'
import { money, toMoney } from './money.js'
import type { PaymentStatus } from './sales.js'
import type { ServiceCustomerRef, ServiceJobStatus, ServiceUserRef } from './service.js'
import { type AssetRef, type CheckItem, checkItemSchema, type PlaceStep } from './service-assets.js'
import type { DocumentApproval } from './workflows.js'

/**
 * Service releases C2–C4: maintenance contracts (docs/refactor/SERVICE_CONTRACTS.md §4.3–§9,
 * service/C2_C4_BREAKDOWN.md). The licensable module `contracts` (needs Service, uses Sales): a contract
 * covers assets at a customer's sites on terms, its plans generate planned Service jobs, the technician
 * checks each asset on the visit, and its instalments are billed as posted Sales invoices.
 *
 * Money (value, tax, instalments, invoices, profitability) is shown only to holders of `contracts.manage`
 * or `contracts.bill`; elsewhere it is null.
 */

const requiredText = (max: number, message = 'Required') => z.string().trim().min(1, message).max(max)
const nullableUuid = z
  .uuid()
  .nullish()
  .transform((v) => v ?? null)
const nullableDate = z.iso
  .date()
  .nullish()
  .transform((v) => v ?? null)
const nonNegativeMoney = moneySchema.refine((v) => Number(v) >= 0, 'The amount cannot be negative')
const positiveMoney = moneySchema.refine((v) => Number(v) > 0, 'Enter an amount above zero')
const uniqueIds = (list: string[]) => new Set(list.map((x) => x.toLowerCase())).size === list.length
const uniqueKeys = (list: { key: string }[]) => new Set(list.map((x) => x.key)).size === list.length
/** `a,b,c` in a query string (GET routes), as a list of ids. */
const idList = (max: number) =>
  z
    .string()
    .trim()
    .optional()
    .transform((v) =>
      v
        ? v
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    )
    .pipe(z.array(z.uuid()).max(max))

type Issue = { path: string; message: string }

// ── limits ───────────────────────────────────────────────────────────────────

/** At most this many sites, picked assets and asset types on one contract. */
export const CONTRACT_MAX_SITES = 50
export const CONTRACT_MAX_ASSETS = 5_000
export const CONTRACT_MAX_TYPES = 100
/** At most this many plans on one contract, and billing items. */
export const CONTRACT_MAX_PLANS = 20
export const CONTRACT_MAX_BILLING_ITEMS = 120
/** Renewal notices go at most this many days before the end. */
export const CONTRACT_NOTICE_MAX_DAYS = 365
/** One invoicing request takes at most this many instalments. */
export const CONTRACT_INVOICE_MAX_ITEMS = 200

// ── statuses and enums ───────────────────────────────────────────────────────

/**
 * DRAFT: editable, never covers a job. ACTIVE: covers jobs from the start to the end date. EXPIRED (the day
 * after the end, by the daily job), TERMINATED (by a user, from an effective date) and CANCELLED (a discarded
 * draft) are final.
 */
export const SERVICE_CONTRACT_STATUSES = ['DRAFT', 'ACTIVE', 'EXPIRED', 'TERMINATED', 'CANCELLED'] as const
export const serviceContractStatusSchema = z.enum(SERVICE_CONTRACT_STATUSES)
export type ServiceContractStatus = z.infer<typeof serviceContractStatusSchema>
export const SERVICE_CONTRACT_FINAL_STATUSES: readonly ServiceContractStatus[] = [
  'EXPIRED',
  'TERMINATED',
  'CANCELLED',
]

/** ALL: every asset at the contract's sites. TYPES: the chosen asset types there. ASSETS: a picked list. */
export const CONTRACT_COVERAGE_MODES = ['ALL', 'TYPES', 'ASSETS'] as const
export const contractCoverageModeSchema = z.enum(CONTRACT_COVERAGE_MODES)
export type ContractCoverageMode = z.infer<typeof contractCoverageModeSchema>

export const CONTRACT_INCLUSIONS = ['INCLUDED', 'EXCLUDED'] as const
export type ContractInclusion = (typeof CONTRACT_INCLUSIONS)[number]
/** CAPPED: parts are covered up to `partsCap` per job or per contract year; past it they are billed. */
export const CONTRACT_PARTS_TERMS = ['INCLUDED', 'EXCLUDED', 'CAPPED'] as const
export type ContractPartsTerm = (typeof CONTRACT_PARTS_TERMS)[number]
export const CONTRACT_CAP_BASES = ['PER_JOB', 'PER_YEAR'] as const
export type ContractCapBasis = (typeof CONTRACT_CAP_BASES)[number]

/** How the value is split into instalments. CUSTOM: none are made; the instalments are entered by hand. */
export const CONTRACT_BILLING_SCHEDULES = [
  'MONTHLY',
  'QUARTERLY',
  'HALF_YEARLY',
  'YEARLY',
  'UPFRONT',
  'CUSTOM',
] as const
export const contractBillingScheduleSchema = z.enum(CONTRACT_BILLING_SCHEDULES)
export type ContractBillingSchedule = z.infer<typeof contractBillingScheduleSchema>
/** ADVANCE: an instalment is billed on its period's first day. ARREARS: on its last day. */
export const CONTRACT_BILLING_TIMINGS = ['ADVANCE', 'ARREARS'] as const
export const contractBillingTimingSchema = z.enum(CONTRACT_BILLING_TIMINGS)
export type ContractBillingTiming = z.infer<typeof contractBillingTimingSchema>

/** How a job item's covered/chargeable default was decided on a contract job (shown as words). */
export const CONTRACT_COVERAGE_REASONS = [
  'LABOUR_INCLUDED',
  'LABOUR_EXCLUDED',
  'CALL_OUTS_EXCLUDED',
  'CALL_OUT_OVER_ALLOWANCE',
  'PARTS_INCLUDED',
  'PARTS_EXCLUDED',
  'PARTS_WITHIN_CAP',
  'PARTS_OVER_CAP',
  'CONSUMABLES_INCLUDED',
  'CONSUMABLES_EXCLUDED',
  /** The office changed the default by hand. */
  'SET_BY_HAND',
] as const
export type ContractCoverageReason = (typeof CONTRACT_COVERAGE_REASONS)[number]

/** Problem codes the contracts API (and Service, for contract jobs and checks) answers with. */
export const CONTRACT_ERROR_CODES = [
  /** 409: the contracts module is off (a job naming a contract, a check refused…). */
  'CONTRACTS_OFF',
  /** 422: a job's contract isn't ACTIVE today. */
  'CONTRACT_NOT_ACTIVE',
  /** 422: the contract doesn't cover the job's site (or is another customer's). */
  'CONTRACT_NOT_FOR_SITE',
  /** 409: the command needs a DRAFT (activate, cancel) or an ACTIVE contract (terminate). */
  'CONTRACT_NOT_DRAFT',
  'CONTRACT_NOT_ACTIVE_STATE',
  /** 409: EXPIRED, TERMINATED and CANCELLED contracts don't change. */
  'CONTRACT_FINAL',
  /** 422: activation needs a site and coverage (the details say what is missing). */
  'CONTRACT_INCOMPLETE',
  /** 409: an approval is open; the contract is frozen until it is decided. */
  'CONTRACT_PENDING_APPROVAL',
  /** 409: it was renewed already (by a successor that isn't cancelled). */
  'ALREADY_RENEWED',
  /** 409: a terminated or cancelled contract isn't renewed. */
  'NOT_RENEWABLE',
  /**
   * 422: a renewal starts after its predecessor's last covered day (renew, edits of either contract's
   * dates, activation).
   */
  'RENEWAL_OVERLAP',
  /** 422: a site an ACTIVE contract covers isn't archived (Service's site archive asks the contracts). */
  'SITE_UNDER_CONTRACT',
  /** 422: a site, type or asset that isn't the customer's, of the contract's sites, or archived. */
  'SITE_NOT_OF_CUSTOMER',
  'ASSET_NOT_AT_SITE',
  /** 409: a site can't leave the contract while it has open planned jobs. */
  'SITE_HAS_OPEN_JOBS',
  /** 409: an occurrence whose job has an attended visit can't be skipped. */
  'OCCURRENCE_STARTED',
  'OCCURRENCE_SKIPPED',
  /** 409: billing items. */
  'BILLING_ITEM_INVOICED',
  'BILLING_ITEM_NOT_READY',
  'BILLING_ITEM_CANCELLED',
  'SALES_MODULE_OFF',
  /** Service C3: per-asset checks. */
  'ASSET_CHECKS_OFF',
  'ASSET_NOT_ON_JOB',
  'ASSET_NOT_IN_VISIT_SCOPE',
  /** 422: a planned job finishes FULL only when every asset is checked (`details.remaining`). */
  'CHECKS_INCOMPLETE',
  /** 422: a place of a visit's scope that isn't of the job's site. */
  'PLACE_NOT_AT_SITE',
] as const
export type ContractErrorCode = (typeof CONTRACT_ERROR_CODES)[number]

// ── dates (shared by the API's generator and billing, and the web's previews) ─

export const PLAN_INTERVAL_UNITS = ['WEEK', 'MONTH', 'YEAR'] as const
export const planIntervalUnitSchema = z.enum(PLAN_INTERVAL_UNITS)
export type PlanIntervalUnit = z.infer<typeof planIntervalUnitSchema>

function parseIso(date: string): { y: number; m: number; d: number } {
  const [y, m, d] = date.split('-').map(Number)
  return { y: y ?? 1970, m: m ?? 1, d: d ?? 1 }
}
function formatIso(y: number, m: number, d: number): string {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}
function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate()
}

/** The ISO date `days` days after (or before) `date`. */
export function addDays(date: string, days: number): string {
  const { y, m, d } = parseIso(date)
  const t = new Date(Date.UTC(y, m - 1, d + days))
  return formatIso(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate())
}

/**
 * The date `count` units after `date`. Months and years keep the day of the month, clamped to the month's
 * last day (31 Jan + 1 month = 28 or 29 Feb). Callers step from the same anchor (`anchor + k × count`), so
 * a clamped month never shifts the later ones.
 */
export function addInterval(date: string, unit: PlanIntervalUnit, count: number): string {
  if (unit === 'WEEK') return addDays(date, count * 7)
  const { y, m, d } = parseIso(date)
  const months = unit === 'MONTH' ? count : count * 12
  const index = y * 12 + (m - 1) + months
  const ny = Math.floor(index / 12)
  const nm = (index % 12) + 1
  return formatIso(ny, nm, Math.min(d, daysInMonth(ny, nm)))
}

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: string, to: string): number {
  const a = parseIso(from)
  const b = parseIso(to)
  return Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86_400_000)
}

export interface PlanPeriod {
  /** The due date: the period's first day. */
  start: string
  /** The day before the next due date. */
  end: string
}

/**
 * The periods of a plan whose due date falls in `[from, to]` (both inclusive), stepping from the first due
 * date. At most `limit` periods (the generator's and the preview's guard).
 */
export function planPeriods(
  plan: { firstDue: string; intervalUnit: PlanIntervalUnit; intervalCount: number },
  from: string,
  to: string,
  limit = 400,
): PlanPeriod[] {
  const out: PlanPeriod[] = []
  for (let k = 0; out.length < limit; k++) {
    const start = addInterval(plan.firstDue, plan.intervalUnit, k * plan.intervalCount)
    if (start > to) break
    if (start < from) continue
    const next = addInterval(plan.firstDue, plan.intervalUnit, (k + 1) * plan.intervalCount)
    out.push({ start, end: addDays(next, -1) })
  }
  return out
}

/** The start of the contract year a date falls in (contract years run from the start date's anniversary). */
export function contractYearStart(startDate: string, date: string): string {
  let k = 0
  while (addInterval(startDate, 'YEAR', k + 1) <= date) k++
  return addInterval(startDate, 'YEAR', k)
}

export interface InstalmentDraft {
  /** 1-based. */
  position: number
  periodStart: string
  periodEnd: string
  billingDate: string
  /** Ex-VAT; the instalments of a schedule add up to the value exactly (the last takes the rounding). */
  amount: string
}

const SCHEDULE_MONTHS: Record<Exclude<ContractBillingSchedule, 'CUSTOM' | 'UPFRONT'>, number> = {
  MONTHLY: 1,
  QUARTERLY: 3,
  HALF_YEARLY: 6,
  YEARLY: 12,
}

/**
 * How many instalments a schedule makes over a period (whatever the value; CUSTOM: none, UPFRONT: one), counted
 * without listing them. Above CONTRACT_MAX_BILLING_ITEMS the contract is refused (`contractErrors`, and the
 * API at activation): `scheduleInstalments` stops at that many.
 */
export function scheduleInstalmentCount(input: {
  schedule: ContractBillingSchedule
  startDate: string
  endDate: string
}): number {
  const { schedule, startDate, endDate } = input
  if (schedule === 'CUSTOM' || endDate < startDate) return 0
  if (schedule === 'UPFRONT') return 1
  const step = SCHEDULE_MONTHS[schedule]
  const a = parseIso(startDate)
  const b = parseIso(endDate)
  const months = (b.y - a.y) * 12 + (b.m - a.m)
  // Every due date up to k × step months stays in a month before the end's: a sure start, then step on.
  let k = Math.max(0, Math.floor(months / step) - 1)
  while (addInterval(startDate, 'MONTH', (k + 1) * step) <= endDate) k++
  return k + 1
}

/** The refusal of a schedule that would make too many instalments (null when it fits). */
export function instalmentCountError(input: {
  value: string
  schedule: ContractBillingSchedule
  startDate: string
  endDate: string
}): Issue | null {
  if (!(Number(input.value) > 0)) return null
  const n = scheduleInstalmentCount(input)
  return n > CONTRACT_MAX_BILLING_ITEMS
    ? { path: 'billingSchedule', message: `Choose a longer billing period: this would make ${n} instalments` }
    : null
}

/**
 * The instalments a schedule makes of a contract's value over its period (Activate and "Rebuild" in the
 * API, the preview on the contract form). UPFRONT: one instalment for the whole period. CUSTOM: none. A
 * last, shorter period is still a full instalment of the equal split. A zero value makes none.
 */
export function scheduleInstalments(input: {
  value: string
  schedule: ContractBillingSchedule
  timing: ContractBillingTiming
  startDate: string
  endDate: string
}): InstalmentDraft[] {
  const { value, schedule, timing, startDate, endDate } = input
  if (schedule === 'CUSTOM' || endDate < startDate || !(Number(value) > 0)) return []
  const periods: PlanPeriod[] =
    schedule === 'UPFRONT'
      ? [{ start: startDate, end: endDate }]
      : planPeriods(
          { firstDue: startDate, intervalUnit: 'MONTH', intervalCount: SCHEDULE_MONTHS[schedule] },
          startDate,
          endDate,
          CONTRACT_MAX_BILLING_ITEMS,
        ).map((p) => ({ start: p.start, end: p.end > endDate ? endDate : p.end }))
  const n = periods.length
  if (n === 0) return []
  const share = money(value).dividedBy(n).toDecimalPlaces(3, 1) // round down; the last takes the rest
  let billed = money(0)
  return periods.map((p, i) => {
    const amount = i === n - 1 ? money(value).minus(billed) : share
    billed = billed.plus(amount)
    return {
      position: i + 1,
      periodStart: p.start,
      periodEnd: p.end,
      billingDate: timing === 'ADVANCE' ? p.start : p.end,
      amount: toMoney(amount),
    }
  })
}

// ── terms ────────────────────────────────────────────────────────────────────

export const serviceContractTermsSchema = z.object({
  labour: z.enum(CONTRACT_INCLUSIONS),
  parts: z.enum(CONTRACT_PARTS_TERMS),
  /** CAPPED only: the covered parts value (ex-VAT) per job or per contract year. */
  partsCap: moneySchema.nullish().transform((v) => v ?? null),
  partsCapBasis: z
    .enum(CONTRACT_CAP_BASES)
    .nullish()
    .transform((v) => v ?? null),
  /** Consumables are catalogue products of the categories listed in Settings › Contracts. */
  consumables: z.enum(CONTRACT_INCLUSIONS),
  callOuts: z.enum(CONTRACT_INCLUSIONS),
  /** INCLUDED only: call-outs covered per contract year; null = unlimited. */
  callOutsPerYear: z
    .number()
    .int()
    .min(1)
    .max(365)
    .nullish()
    .transform((v) => v ?? null),
  /** The promised response time in hours (shown on the job). */
  responseHours: z
    .number()
    .int()
    .min(1)
    .max(720)
    .nullish()
    .transform((v) => v ?? null),
  /** What the common terms don't say; printed on the contract. */
  specialTerms: optionalText(4000),
})
export type ServiceContractTermsInput = z.input<typeof serviceContractTermsSchema>
export type ServiceContractTerms = z.output<typeof serviceContractTermsSchema>

/** Cross-field rules of the terms (also applied by the API and the database). */
export function contractTermsErrors(t: ServiceContractTerms, prefix = 'terms'): Issue[] {
  const errors: Issue[] = []
  if (t.parts === 'CAPPED') {
    if (t.partsCap === null || !(Number(t.partsCap) > 0))
      errors.push({ path: `${prefix}.partsCap`, message: 'Enter the parts cap' })
    if (t.partsCapBasis === null)
      errors.push({ path: `${prefix}.partsCapBasis`, message: 'Per job or per contract year?' })
  } else {
    if (t.partsCap !== null)
      errors.push({ path: `${prefix}.partsCap`, message: 'Only capped parts have a cap' })
    if (t.partsCapBasis !== null)
      errors.push({ path: `${prefix}.partsCapBasis`, message: 'Only capped parts have a cap' })
  }
  if (t.callOuts === 'EXCLUDED' && t.callOutsPerYear !== null) {
    errors.push({ path: `${prefix}.callOutsPerYear`, message: 'Excluded call-outs have no allowance' })
  }
  return errors
}

export const DEFAULT_CONTRACT_TERMS: ServiceContractTerms = {
  labour: 'INCLUDED',
  parts: 'EXCLUDED',
  partsCap: null,
  partsCapBasis: null,
  consumables: 'INCLUDED',
  callOuts: 'INCLUDED',
  callOutsPerYear: null,
  responseHours: null,
  specialTerms: null,
}

// ── settings (setting group `contracts`) ─────────────────────────────────────

export const contractSettingsSchema = z.object({
  /** Renewal notices go this many days before the end (each once). */
  renewalNoticeDays: z
    .array(z.number().int().min(1).max(CONTRACT_NOTICE_MAX_DAYS))
    .max(3)
    .refine((l) => new Set(l).size === l.length, 'Each day count once'),
  /** Planned jobs are made this many days before they are due. */
  planAheadDays: z.number().int().min(1).max(90),
  /** The new contract's defaults. */
  billingSchedule: contractBillingScheduleSchema,
  billingTiming: contractBillingTimingSchema,
  terms: serviceContractTermsSchema,
  /** Catalogue categories whose products are consumables (filters, gas, cleaning chemicals). */
  consumableCategoryIds: z.array(z.uuid()).max(50).refine(uniqueIds, 'Each category once'),
})
export type ContractSettings = z.output<typeof contractSettingsSchema>
export const DEFAULT_CONTRACT_SETTINGS: ContractSettings = {
  renewalNoticeDays: [60, 30],
  planAheadDays: 14,
  billingSchedule: 'QUARTERLY',
  billingTiming: 'ADVANCE',
  terms: DEFAULT_CONTRACT_TERMS,
  consumableCategoryIds: [],
}

// ── plans (C3; stored with the contract from C2) ─────────────────────────────

/** ALL: every covered asset. TYPES: the covered assets of the chosen types. */
export const PLAN_SCOPES = ['ALL', 'TYPES'] as const
export type PlanScope = (typeof PLAN_SCOPES)[number]
/** TYPE: each asset type's own checklist. CUSTOM: the plan's list, for every asset it covers. */
export const PLAN_CHECKLIST_MODES = ['TYPE', 'CUSTOM'] as const
export type PlanChecklistMode = (typeof PLAN_CHECKLIST_MODES)[number]

const planFields = z.object({
  /** "Quarterly full service", "Monthly filter cleaning". */
  name: requiredText(120),
  nameAr: optionalText(120),
  scope: z.enum(PLAN_SCOPES),
  typeIds: z.array(z.uuid()).max(CONTRACT_MAX_TYPES).default([]).refine(uniqueIds, 'Each type once'),
  intervalUnit: planIntervalUnitSchema,
  intervalCount: z.number().int().min(1).max(60),
  /** Within the contract period. */
  firstDue: z.iso.date(),
  checklistMode: z.enum(PLAN_CHECKLIST_MODES),
  checklist: z.array(checkItemSchema).max(40).default([]).refine(uniqueKeys, 'Each item needs its own key'),
  /** The planning estimate per asset (the board's "about 3 days of work"). */
  minutesPerAsset: z
    .number()
    .int()
    .min(1)
    .max(600)
    .nullish()
    .transform((v) => v ?? null),
})
type PlanFields = z.output<typeof planFields>
/** Cross-field rules of a plan. */
export function planErrors(p: PlanFields, prefix = ''): Issue[] {
  const errors: Issue[] = []
  if (p.scope === 'TYPES' && p.typeIds.length === 0)
    errors.push({ path: `${prefix}typeIds`, message: 'Choose the asset types the plan covers' })
  if (p.scope === 'ALL' && p.typeIds.length > 0)
    errors.push({ path: `${prefix}typeIds`, message: 'A plan for every asset has no types' })
  if (p.checklistMode === 'CUSTOM' && p.checklist.length === 0)
    errors.push({ path: `${prefix}checklist`, message: 'Add the checklist items' })
  if (p.checklistMode === 'TYPE' && p.checklist.length > 0)
    errors.push({ path: `${prefix}checklist`, message: "The types' own checklists are used" })
  return errors
}
const refinePlan = (v: PlanFields, ctx: z.RefinementCtx) => {
  for (const e of planErrors(v)) ctx.addIssue({ code: 'custom', path: [e.path], message: e.message })
}
/** `POST /contracts/:id/plans` (contracts.manage), and each plan of a new contract. */
export const contractPlanInputSchema = planFields.superRefine(refinePlan)
export type ContractPlanInput = z.input<typeof contractPlanInputSchema>
/** `PATCH /contracts/:id/plans/:planId`. Occurrences already made keep their jobs. */
export const contractPlanUpdateSchema = planFields.extend({ version: versionSchema }).superRefine(refinePlan)
export type ContractPlanUpdate = z.input<typeof contractPlanUpdateSchema>
/** `POST …/plans/:planId/pause` and `…/resume`. */
export const contractPlanCommandSchema = z.object({ version: versionSchema })

// ── the contract ─────────────────────────────────────────────────────────────

const contractFields = z.object({
  /** "Al Noor Tower: AC maintenance". */
  title: optionalText(200),
  startDate: z.iso.date(),
  endDate: z.iso.date(),
  /** The customer's sites (a DRAFT may have none yet; activation needs one). */
  siteIds: z.array(z.uuid()).max(CONTRACT_MAX_SITES).default([]).refine(uniqueIds, 'Each site once'),
  coverageMode: contractCoverageModeSchema,
  /** TYPES only. */
  typeIds: z.array(z.uuid()).max(CONTRACT_MAX_TYPES).default([]).refine(uniqueIds, 'Each type once'),
  /** ASSETS only: each at one of the sites. */
  assetIds: z.array(z.uuid()).max(CONTRACT_MAX_ASSETS).default([]).refine(uniqueIds, 'Each asset once'),
  terms: serviceContractTermsSchema,
  /** Ex-VAT, for the whole period. */
  value: nonNegativeMoney,
  /** null: the company's default sales tax rate. */
  taxRateId: nullableUuid,
  billingSchedule: contractBillingScheduleSchema,
  billingTiming: contractBillingTimingSchema,
  /** Who sold the contract (ADR-049): its instalment invoices credit them. Omitted = the customer's. */
  salespersonId: z.uuid().nullish(),
  notes: optionalText(2000),
})
type ContractFields = z.output<typeof contractFields>
/** Cross-field rules of a contract (also applied by the API). */
export function contractErrors(v: ContractFields): Issue[] {
  const errors: Issue[] = [...contractTermsErrors(v.terms)]
  if (v.endDate < v.startDate) errors.push({ path: 'endDate', message: 'The end is before the start' })
  if (v.coverageMode === 'TYPES' && v.typeIds.length === 0)
    errors.push({ path: 'typeIds', message: 'Choose the asset types it covers' })
  if (v.coverageMode !== 'TYPES' && v.typeIds.length > 0)
    errors.push({ path: 'typeIds', message: 'Only coverage by type lists types' })
  if (v.coverageMode === 'ASSETS' && v.assetIds.length === 0)
    errors.push({ path: 'assetIds', message: 'Pick the assets it covers' })
  if (v.coverageMode !== 'ASSETS' && v.assetIds.length > 0)
    errors.push({ path: 'assetIds', message: 'Only a picked list lists assets' })
  const tooMany = instalmentCountError({
    value: v.value,
    schedule: v.billingSchedule,
    startDate: v.startDate,
    endDate: v.endDate,
  })
  if (tooMany) errors.push(tooMany)
  return errors
}
const refineContract = (v: ContractFields, ctx: z.RefinementCtx) => {
  for (const e of contractErrors(v))
    ctx.addIssue({ code: 'custom', path: e.path.split('.'), message: e.message })
}

/** `POST /contracts` (contracts.manage): a DRAFT, numbered at once (AMC-2026-00001), with its plans. */
export const contractCreateSchema = contractFields
  .extend({
    customerId: z.uuid(),
    plans: z.array(contractPlanInputSchema).max(CONTRACT_MAX_PLANS).default([]),
  })
  .superRefine(refineContract)
export type ContractCreateInput = z.input<typeof contractCreateSchema>

/**
 * `PATCH /contracts/:id` (contracts.manage). DRAFT: everything. ACTIVE: not the start date, and the end only
 * later; a site with open planned jobs can't leave (SITE_HAS_OPEN_JOBS). Final contracts: 409 CONTRACT_FINAL.
 * Plans change through their own routes.
 */
export const contractUpdateSchema = contractFields
  .extend({ version: versionSchema })
  .superRefine(refineContract)
export type ContractUpdateInput = z.input<typeof contractUpdateSchema>

/** `POST /contracts/:id/activate`: DRAFT → ACTIVE, or the approval run when a workflow applies (C4). */
export const serviceContractActivateSchema = z.object({ version: versionSchema })
/** `POST /contracts/:id/cancel`: a DRAFT is discarded (kept as CANCELLED, its number used). */
export const contractCancelSchema = z.object({
  version: versionSchema,
  reason: requiredText(500, 'Give a reason'),
})
/**
 * `POST /contracts/:id/terminate`: ACTIVE → TERMINATED from `effectiveDate` (start ≤ date ≤ end). Cancels
 * the un-invoiced instalments whose period starts after it and the planned jobs due after it that have no
 * visit (`GET /contracts/:id/terminate/preview?effectiveDate=` lists them first).
 */
export const contractTerminateSchema = z.object({
  version: versionSchema,
  effectiveDate: z.iso.date(),
  reason: requiredText(1000, 'Give a reason'),
})
export type ContractTerminateInput = z.input<typeof contractTerminateSchema>
export const contractTerminatePreviewQuerySchema = z.object({ effectiveDate: z.iso.date() })
export interface ContractTerminatePreview {
  effectiveDate: string
  /** Planned jobs that will be cancelled. */
  jobs: { id: string; number: string; dueDate: string; siteName: string }[]
  /** Instalments that will be cancelled (money rights; otherwise only the count). */
  instalments: { id: string; label: string; amount: string | null }[]
}

/**
 * `POST /contracts/:id/renew` (ACTIVE or EXPIRED): the next DRAFT, starting the day after the end and of
 * the same length unless dates are given, copying sites, coverage, terms, plans and billing, with the value
 * changed by `priceChangePercent` (e.g. "5", "-2.5"), rounded to the baisa.
 */
export const contractRenewSchema = z
  .object({
    startDate: nullableDate,
    endDate: nullableDate,
    priceChangePercent: z
      .string()
      .regex(/^-?\d{1,3}(\.\d{1,2})?$/, 'Invalid percentage')
      .refine((v) => Number(v) > -100, 'The price cannot fall by 100 % or more')
      .default('0'),
  })
  .refine((v) => v.startDate === null || v.endDate === null || v.endDate >= v.startDate, {
    path: ['endDate'],
    message: 'The end is before the start',
  })
export type ContractRenewInput = z.input<typeof contractRenewSchema>

/**
 * `POST /contracts/:id/generate`: the generator for this contract now, as of today (Muscat): the route takes
 * no date, so it never skips a period by planning from a later day. Answers `ContractGenerateResult`.
 */
export const contractGenerateSchema = z.object({})
export interface ContractGenerateResult {
  /** New occurrences (each with its new planned job). */
  created: number
  /** Periods that already had their occurrence. */
  existing: number
  /** Plans that failed, with the reason. */
  failed: { planId: string; message: string }[]
}

// ── lists ────────────────────────────────────────────────────────────────────

/** Register views; each a filter whose count shows on its tab. EXPIRING: ACTIVE and ending within 60 days. */
export const CONTRACT_VIEWS = ['active', 'expiring', 'draft', 'ended', 'all'] as const
export type ContractView = (typeof CONTRACT_VIEWS)[number]
export const CONTRACT_SORTS = [
  'number',
  '-number',
  'customer',
  '-customer',
  'endDate',
  '-endDate',
  'startDate',
  '-startDate',
  'value',
  '-value',
] as const
export type ContractSort = (typeof CONTRACT_SORTS)[number]
/**
 * `GET /contracts?view&customerId&siteId&q&sort&page&pageSize`: `q` matches the number, title and the
 * customer's name and code. Sorting by value needs money rights (otherwise it sorts by number).
 */
export const contractListQuerySchema = pageQuerySchema.extend({
  view: z.enum(CONTRACT_VIEWS).default('active'),
  customerId: z.uuid().optional(),
  siteId: z.uuid().optional(),
  sort: z.enum(CONTRACT_SORTS).default('-number'),
})
export type ContractListQuery = z.input<typeof contractListQuerySchema>
export const contractCountsQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  customerId: z.uuid().optional(),
  siteId: z.uuid().optional(),
})
export type ContractCounts = Record<ContractView, number>

// ── refs and the punch strip ─────────────────────────────────────────────────

export interface ContractRef {
  id: string
  number: string
  title: string | null
  status: ServiceContractStatus
  startDate: string
  endDate: string
}

/**
 * The state of one period on the punch strip (the maintenance tag). DONE: every occurrence of the period
 * done; PARTIAL: some done or under way; PLANNED: not due yet; LATE: due and not done, period still running;
 * MISSED: the period ended undone; SKIPPED: skipped with a reason; NONE: nothing was due (no covered assets).
 */
export const PUNCH_STATES = ['DONE', 'PARTIAL', 'PLANNED', 'LATE', 'MISSED', 'SKIPPED', 'NONE'] as const
export type PunchState = (typeof PUNCH_STATES)[number]
export interface PunchCell {
  periodStart: string
  periodEnd: string
  state: PunchState
  /** Today falls in this period. */
  current: boolean
  /** Occurrences of the period, and how many are done. */
  due: number
  done: number
  /** SKIPPED: the skip reason (the first, when several were skipped); otherwise absent or null. */
  reason?: string | null
  /**
   * An occurrence of the period is past its due date and not done or skipped (also while it is under way,
   * so a PARTIAL cell can be late). Optional for older fixtures.
   */
  late?: boolean
}
export interface PunchStrip {
  unit: PlanIntervalUnit
  count: number
  cells: PunchCell[]
}

/** Planned visits that were due (on or before today), done and skipped; percent = done ÷ (due − skipped). */
export interface ComplianceFigure {
  due: number
  done: number
  skipped: number
  /** "92.5"; null when nothing counted yet. */
  percent: string | null
}

// ── the contract as read ─────────────────────────────────────────────────────

export interface ContractTaxRef {
  id: string
  name: string
  nameAr: string | null
  rate: string
}

export interface ContractSite {
  id: string
  name: string
  code: string | null
  area: string | null
  archived: boolean
  /** Assets in service or out of order at the site, and how many the contract covers. */
  assetCount: number
  coveredCount: number
}

export interface ContractPlan {
  id: string
  name: string
  nameAr: string | null
  scope: PlanScope
  types: { id: string; code: string; name: string; nameAr: string | null }[]
  intervalUnit: PlanIntervalUnit
  intervalCount: number
  firstDue: string
  checklistMode: PlanChecklistMode
  checklist: CheckItem[]
  minutesPerAsset: number | null
  active: boolean
  position: number
  /** Covered assets in the plan's scope now. */
  assetCount: number
  /**
   * The next visit due: the earliest due date of an occurrence not done or skipped (it may be past: overdue),
   * else the next date the generator will make; null only after the contract's last period.
   */
  nextDue: string | null
  /** From the contract's start (at most 24 cells, the current one included). */
  strip: PunchStrip
  compliance: ComplianceFigure
  lastRunAt: string | null
  /** The generator's last error on this plan. */
  lastError: string | null
  version: number
}

export interface ContractCapabilities {
  edit: boolean
  activate: boolean
  cancel: boolean
  terminate: boolean
  renew: boolean
  managePlans: boolean
  generate: boolean
  /** Value, tax, instalments, invoices. */
  viewMoney: boolean
  /** Instalments and invoicing (contracts.bill). */
  bill: boolean
  skipOccurrences: boolean
}

export interface ContractListItem extends ContractRef {
  customer: ServiceCustomerRef
  /** Who sold the contract (ADR-049). Always sent; optional for older fixtures. */
  salesperson?: ServiceUserRef | null
  /** The first three sites by name, and how many there are. */
  sites: { id: string; name: string }[]
  siteCount: number
  /** Days from today to the end (negative once ended); null for final contracts. */
  daysToEnd: number | null
  /** Money rights only. */
  value: string | null
  /**
   * The last 6 periods of the plan that needs the most attention (the lowest compliance, an active plan
   * first; see `recentPlan`), and the contract's compliance to date.
   */
  recent: PunchCell[]
  /** The interval of `recent`'s plan, for its labels (null without plans; optional for older fixtures). */
  recentInterval?: { unit: PlanIntervalUnit; count: number } | null
  compliance: ComplianceFigure
  /** The plan `recent` belongs to (null without plans; optional for older fixtures). */
  recentPlan?: { id: string; name: string; nameAr: string | null } | null
  /** The next planned visit due: the earliest occurrence not done or skipped (past = overdue). */
  nextDue: string | null
  renewedBy: ContractRef | null
  renews: ContractRef | null
  /** An approval run is open (C4). */
  pendingApproval: boolean
  createdAt: string
}

export interface ContractFigures {
  /** Money rights only (else null). */
  value: string | null
  tax: string | null
  /** Posted instalment invoices, collected on them, and their balance. */
  invoiced: string | null
  collected: string | null
  balance: string | null
  /** Planned visits done and due to date. */
  visitsDone: number
  visitsDue: number
  /** Assets whose latest check found an issue that has no completed fault job. */
  openIssues: number
  /** REQUEST jobs on the contract this contract year, and the allowance (null = unlimited or excluded). */
  callOutsUsed: number
  callOutsAllowance: number | null
}

export interface ContractDetail extends ContractListItem {
  coverageMode: ContractCoverageMode
  siteDetails: ContractSite[]
  types: { id: string; code: string; name: string; nameAr: string | null }[]
  /** ASSETS: the picked list's size. */
  pickedAssetCount: number
  /**
   * ASSETS: every picked asset's id, covered now or not (removed or replaced ones too), so an edit sends the
   * list back unchanged; [] in the other modes. Optional for older fixtures.
   */
  pickedAssetIds?: string[]
  /** Assets covered now (live). */
  coveredAssetCount: number
  terms: ServiceContractTerms
  taxRate: ContractTaxRef | null
  billingSchedule: ContractBillingSchedule
  billingTiming: ContractBillingTiming
  notes: string | null
  plans: ContractPlan[]
  figures: ContractFigures
  /** The contract year today falls in (ACTIVE contracts). */
  contractYear: { start: string; end: string } | null
  activatedAt: string | null
  activatedBy: ServiceUserRef | null
  terminatedOn: string | null
  terminationReason: string | null
  terminatedBy: ServiceUserRef | null
  cancelReason: string | null
  cancelledAt: string | null
  createdBy: ServiceUserRef | null
  capabilities: ContractCapabilities
  /**
   * C4: the latest approval run of the contract (as purchases and budgets carry it), null without one.
   * Optional for older fixtures.
   */
  approval?: DocumentApproval | null
  version: number
}

/** `GET /contracts/:id/assets?siteId&placeId&typeId&q&page&pageSize`: the covered assets, live. */
export const contractAssetsQuerySchema = pageQuerySchema.extend({
  siteId: z.uuid().optional(),
  typeId: z.uuid().optional(),
})
export type ContractAssetsQuery = z.input<typeof contractAssetsQuerySchema>
export interface ContractAsset extends AssetRef {
  site: { id: string; name: string }
  warrantyUntil: string | null
  lastCheck: { date: string; outcome: CheckOutcome } | null
}

// ── the covered check (new jobs) and the job's contract ──────────────────────

/**
 * `GET /contracts/coverage?siteId=…&assetIds=a,b` (contracts.read, service.read or service.create; module
 * contracts): the site's contracts ACTIVE today, and what each covers of the given assets. No money.
 */
export const contractCoverageQuerySchema = z.object({
  siteId: z.uuid(),
  assetIds: idList(200),
})
export type ContractCoverageQuery = z.input<typeof contractCoverageQuerySchema>
export interface ContractCoverageEntry {
  contract: ContractRef
  terms: ServiceContractTerms
  coveredAssetIds: string[]
  notCoveredAssetIds: string[]
  callOuts: {
    /** REQUEST jobs on the contract in the current contract year (not cancelled). */
    used: number
    allowance: number | null
    /** The next call-out would pass the allowance. */
    nextOverAllowance: boolean
  }
}
export interface ContractCoverage {
  contracts: ContractCoverageEntry[]
  /** The one contract a new job should take: it covers every given asset (or it is the site's only one). */
  suggestedId: string | null
}

/**
 * `GET /contracts/assets/:assetId/coverage` (contracts.read or service.read; module contracts): the contracts
 * covering the asset now (ACTIVE, or DRAFT for planning) and the plans that reach it. No money.
 */
export interface AssetContractCoverage {
  contracts: {
    contract: ContractRef
    terms: ServiceContractTerms
    plans: {
      id: string
      name: string
      nameAr: string | null
      intervalUnit: PlanIntervalUnit
      intervalCount: number
      nextDue: string | null
      strip: PunchStrip
    }[]
  }[]
}

/** The contract block of a Service job (no money). */
export interface JobContract extends ContractRef {
  terms: ServiceContractTerms
  callOuts: { used: number; allowance: number | null }
  /** This job passed the year's call-out allowance when it was opened. */
  overAllowance: boolean
  /** Of the job's assets, those the contract covers. */
  coveredAssetIds: string[]
  /** A planned job: its plan and period. */
  plan: {
    id: string
    name: string
    nameAr: string | null
    periodStart: string
    periodEnd: string
    dueDate: string
    occurrenceId: string
  } | null
  /** The viewer may open the contract (contracts.read). */
  openable: boolean
}

// ── occurrences (C3) ─────────────────────────────────────────────────────────

/** Derived from the occurrence's job; see PUNCH_STATES for LATE and MISSED. */
export const CONTRACT_OCCURRENCE_STATUSES = [
  'PLANNED',
  'SCHEDULED',
  'IN_PROGRESS',
  'DONE',
  'LATE',
  'MISSED',
  'SKIPPED',
] as const
export type ContractOccurrenceStatus = (typeof CONTRACT_OCCURRENCE_STATUSES)[number]

/** `GET /contracts/:id/occurrences?planId&siteId&status&from&to&page&pageSize`: by due date, latest first. */
export const occurrenceListQuerySchema = pageQuerySchema.extend({
  planId: z.uuid().optional(),
  siteId: z.uuid().optional(),
  status: z.enum(CONTRACT_OCCURRENCE_STATUSES).optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
})
export type OccurrenceListQuery = z.input<typeof occurrenceListQuerySchema>

export interface ContractOccurrence {
  id: string
  contractId: string
  plan: { id: string; name: string; nameAr: string | null }
  site: { id: string; name: string }
  periodStart: string
  periodEnd: string
  dueDate: string
  status: ContractOccurrenceStatus
  job: {
    id: string
    number: string
    status: ServiceJobStatus
    completedAt: string | null
    visitCount: number
    /** The next open visit's date. */
    nextVisit: string | null
    /** Assets checked on any visit of the job, of its assets. */
    checked: number
    total: number
  }
  assetCount: number
  /** Past its due date and not done or skipped (also while under way). Optional for older fixtures. */
  overdue?: boolean
  skipReason: string | null
  skippedAt: string | null
  skippedBy: ServiceUserRef | null
}

/**
 * `POST /contracts/occurrences/:id/skip` (contracts.manage or service.manage): cancels its job too; answers
 * `ContractOccurrence`.
 */
export const occurrenceSkipSchema = z.object({ reason: requiredText(500, 'Give a reason') })

// ── per-asset checks on a visit (C3, Service routes) ─────────────────────────

/** DONE: serviced. ISSUE: a fault found. NO_ACCESS: couldn't reach it. SKIPPED: not done, with a reason. */
export const CHECK_OUTCOMES = ['DONE', 'ISSUE', 'NO_ACCESS', 'SKIPPED'] as const
export const checkOutcomeSchema = z.enum(CHECK_OUTCOMES)
export type CheckOutcome = z.infer<typeof checkOutcomeSchema>

/** A checklist item's result: done or not (check), or the reading as a decimal string. */
export const checkResultSchema = z.union([
  z.boolean(),
  z.string().regex(/^-?\d{1,9}(\.\d{1,3})?$/, 'Enter a number'),
])
export type CheckResult = z.infer<typeof checkResultSchema>

const checkFields = z.object({
  outcome: checkOutcomeSchema,
  /** Keyed by checklist item key; keys outside the asset's checklist are refused (422). */
  results: z.record(z.string().max(40), checkResultSchema).default({}),
  note: optionalText(1000),
  /** Required for NO_ACCESS and SKIPPED ("Tenant away", "Unit locked"). */
  reason: optionalText(500),
})
/**
 * `PUT /service/visits/:id/checks/:assetId` (service.work or service.manage; the visit ON_SITE or
 * IN_PROGRESS): records or replaces this visit's check of the asset. Idempotent: the phone retries freely.
 * Answers the `VisitCheck` (200).
 */
export const visitCheckInputSchema = checkFields
  .refine((v) => (v.outcome !== 'NO_ACCESS' && v.outcome !== 'SKIPPED') || Boolean(v.reason), {
    path: ['reason'],
    message: 'Say why',
  })
  .refine((v) => v.outcome !== 'ISSUE' || Boolean(v.note), {
    path: ['note'],
    message: 'Describe the issue',
  })
export type VisitCheckInput = z.input<typeof visitCheckInputSchema>
/**
 * `POST /service/visits/:id/checks`: several assets DONE at once ("All done in 1204"); existing checks stay.
 * Answers `VisitCheck[]` for the listed assets, in request order (201).
 */
export const visitChecksBulkSchema = z.object({
  assetIds: z.array(z.uuid()).min(1).max(500).refine(uniqueIds, 'Each asset once'),
})
export type VisitChecksBulkInput = z.input<typeof visitChecksBulkSchema>
/**
 * `POST /service/visits/:id/checks/:assetId/fault`: a REQUEST job on the asset (CONTRACT under the same
 * contract when it covers the asset), linked to the check; the issue defaults to the check's note. Answers
 * the `VisitCheck` with `faultJob` (201); again: the same link. 404 without a check, 422 (`outcome`) unless
 * ISSUE.
 */
export const visitCheckFaultSchema = z.object({
  issue: optionalText(2000),
  priority: z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT']).default('NORMAL'),
})
export type VisitCheckFaultInput = z.input<typeof visitCheckFaultSchema>

export interface VisitCheck {
  id: string
  visitId: string
  assetId: string
  outcome: CheckOutcome
  results: Record<string, CheckResult>
  note: string | null
  reason: string | null
  faultJob: { id: string; number: string; status: ServiceJobStatus } | null
  checkedBy: ServiceUserRef
  checkedAt: string
}

export interface VisitChecklistAsset {
  asset: AssetRef
  /** The plan's CUSTOM list on a planned job, else the asset type's checklist. */
  checklist: CheckItem[]
  /** This visit's check. */
  check: VisitCheck | null
  /** The latest check of the asset on an earlier visit of the job (a split visit). */
  earlier: { visitId: string; date: string; outcome: CheckOutcome } | null
  /** Inside the visit's places (always true without a scope). */
  inScope: boolean
}

export interface VisitChecklist {
  /** The Service switch `assetChecklists` is on and the job has assets. */
  enabled: boolean
  /** A planned job: FULL needs every asset checked. */
  required: boolean
  /** The visit's places (empty: the whole job). */
  scope: { id: string; path: string; steps: PlaceStep[] }[]
  assets: VisitChecklistAsset[]
  progress: {
    /** Of the assets in scope, checked on this visit. */
    visit: { checked: number; total: number }
    /** Of the job's assets, checked on any visit. */
    job: { checked: number; total: number }
  }
}

// ── billing (C4) ─────────────────────────────────────────────────────────────

/**
 * Derived, never stored. CANCELLED; INVOICED: an active line of a contract invoice; READY: the billing date
 * is today or earlier; PLANNED otherwise.
 */
export const CONTRACT_BILLING_STATUSES = ['PLANNED', 'READY', 'INVOICED', 'CANCELLED'] as const
export type ContractBillingStatus = (typeof CONTRACT_BILLING_STATUSES)[number]

const billingItemFields = z.object({
  /** "Q1 2026", "January 2026". */
  label: requiredText(120),
  periodStart: z.iso.date(),
  periodEnd: z.iso.date(),
  billingDate: z.iso.date(),
  amount: positiveMoney,
  /** null: the contract's tax rate. */
  taxRateId: nullableUuid,
})
const periodOk = (v: { periodStart: string; periodEnd: string }) => v.periodEnd >= v.periodStart
const periodRule = { path: ['periodEnd'], message: 'The end is before the start' }
/** `POST /contracts/:id/billing-items` (contracts.manage or contracts.bill): added last. */
export const contractBillingItemInputSchema = billingItemFields.refine(periodOk, periodRule)
export type ContractBillingItemInput = z.input<typeof contractBillingItemInputSchema>
/** `PATCH …/billing-items/:itemId`: an INVOICED or CANCELLED item doesn't change (409). */
export const contractBillingItemUpdateSchema = billingItemFields
  .extend({ version: versionSchema })
  .refine(periodOk, periodRule)
export type ContractBillingItemUpdate = z.input<typeof contractBillingItemUpdateSchema>
/** `POST …/billing-items/:itemId/cancel`. */
export const contractBillingItemCancelSchema = z.object({
  version: versionSchema,
  reason: requiredText(500, 'Give a reason'),
})
/** `POST /contracts/:id/billing-items/rebuild`: replaces the un-invoiced items from the schedule. */
export const contractBillingRebuildSchema = z.object({ version: versionSchema })

export interface ContractInvoiceRef {
  id: string
  number: string | null
  invoiceDate: string
  status: 'DRAFT' | 'POSTED' | 'VOID'
}

export interface ContractBillingItem {
  id: string
  position: number
  label: string
  periodStart: string
  periodEnd: string
  billingDate: string
  amount: string
  /** The rate that applies (the item's own, else the contract's, else the company default). */
  taxRate: ContractTaxRef | null
  /**
   * The item's own rate as stored: null = it follows the contract's (an edit sends this back unchanged).
   * Optional for older fixtures.
   */
  taxRateId?: string | null
  tax: string
  total: string
  status: ContractBillingStatus
  invoice: ContractInvoiceRef | null
  cancelReason: string | null
  cancelledAt: string | null
  version: number
}

export interface ContractBilling {
  items: ContractBillingItem[]
  /** Instalments not cancelled, ex-VAT, against the contract value; a difference is a warning. */
  scheduled: string
  value: string
  difference: string
  invoiced: string
  collected: string
  balance: string
}

/** `GET /contracts/billing/ready?customerId&contractId&q&page&pageSize` (contracts.bill): READY instalments. */
export const readyInstalmentsQuerySchema = pageQuerySchema.extend({
  customerId: z.uuid().optional(),
  contractId: z.uuid().optional(),
})
export type ReadyInstalmentsQuery = z.input<typeof readyInstalmentsQuerySchema>
export interface ReadyInstalment extends ContractBillingItem {
  contract: ContractRef
  customer: ServiceCustomerRef
}

const invoiceRequestFields = z.object({
  itemIds: z.array(z.uuid()).min(1).max(CONTRACT_INVOICE_MAX_ITEMS).refine(uniqueIds, 'Each instalment once'),
  /** Default: today. */
  invoiceDate: nullableDate,
  notes: optionalText(1000),
})
/** `POST /contracts/billing/preview` (contracts.bill): the invoices it would make; writes nothing. */
export const contractInvoicePreviewSchema = invoiceRequestFields
export type ContractInvoicePreviewInput = z.input<typeof contractInvoicePreviewSchema>
/**
 * `POST /contracts/billing/invoice` (contracts.bill; Sales on): one posted invoice per contract, one line per
 * instalment. The same `requestId` again returns the invoices it made (`created: false`).
 */
export const contractInvoiceSchema = invoiceRequestFields.extend({ requestId: z.uuid() })
export type ContractInvoiceInput = z.input<typeof contractInvoiceSchema>

export interface ContractInvoiceDraft {
  contract: ContractRef
  customer: ServiceCustomerRef
  lines: {
    itemId: string
    description: string
    amount: string
    taxRate: ContractTaxRef | null
    tax: string
  }[]
  subtotal: string
  tax: string
  total: string
}
export interface ContractInvoicePreview {
  invoiceDate: string
  invoices: ContractInvoiceDraft[]
  /** Per instalment (`itemIds.N`): "Already invoiced", "Not ready to invoice yet", "This item is cancelled". */
  errors: Issue[]
}
export interface ContractInvoiceResult {
  invoices: {
    contractId: string
    /** The contract's number (AMC-…); optional for older fixtures. */
    contractNumber?: string
    invoiceId: string
    number: string
    total: string
    created: boolean
  }[]
}

/** `GET /contracts/:id/invoices`: the contract's invoices, newest first. */
export interface ContractInvoiceDoc extends ContractInvoiceRef {
  total: string
  paid: string
  balance: string
  paymentStatus: PaymentStatus | null
  lines: { itemId: string; label: string; amount: string }[]
  createdBy: ServiceUserRef | null
  createdAt: string
}

// ── insights (C4) ────────────────────────────────────────────────────────────

/** What needs attention, each with one action. */
export const CONTRACT_INSIGHT_CODES = [
  /** A planned visit past its due date and not done → open the job (schedule it). */
  'VISIT_OVERDUE',
  /** A contract ending within the first notice days, not renewed → Renew. */
  'RENEWAL_DUE',
  /** Instalments READY to invoice → the billing queue (contracts.bill). */
  'BILLING_READY',
  /** An asset whose warranty ends within 30 days → open the asset. */
  'WARRANTY_EXPIRING',
  /** An asset with 3 or more issues or fault jobs in 90 days → open the asset. */
  'PROBLEM_ASSET',
] as const
export type ContractInsightCode = (typeof CONTRACT_INSIGHT_CODES)[number]

export interface ContractAttention {
  code: ContractInsightCode
  /** What it is about: a job, contract or asset id. */
  subjectId: string
  /** Words for the sentence (numbers, names, dates), by key; the web words it. */
  params: Record<string, string | number | null>
  /** The date it is about (due date, end date, warranty end). */
  date: string | null
}

export interface ContractInsights {
  today: string
  attention: ContractAttention[]
  /** ACTIVE contracts, worst compliance first: their last 12 periods. */
  compliance: {
    contract: ContractRef
    customer: ServiceCustomerRef
    /** The plan the cells belong to (the one needing the most attention); optional for older fixtures. */
    plan?: { id: string; name: string; nameAr: string | null } | null
    /** That plan's interval, for the cells' period names; optional for older fixtures. */
    interval?: { unit: PlanIntervalUnit; count: number } | null
    cells: PunchCell[]
    figure: ComplianceFigure
  }[]
  /** Most issues and fault jobs in 90 days (at most 20). */
  problemAssets: {
    asset: AssetRef
    site: { id: string; name: string }
    issues: number
    faultJobs: number
    lastAt: string
  }[]
  /** Warranty ending within 60 days. */
  warranty: { asset: AssetRef; site: { id: string; name: string }; warrantyUntil: string; daysLeft: number }[]
  /** ACTIVE contracts ending within 90 days. */
  renewals: {
    contract: ContractRef
    customer: ServiceCustomerRef
    daysLeft: number
    renewal: ContractRef | null
    value: string | null
  }[]
  /**
   * Money rights only (else null). Invoiced: instalment invoices plus chargeable invoices of the contract's
   * jobs; covered parts: done covered GOODS items at their average cost (an estimate).
   */
  profitability:
    | {
        contract: ContractRef
        customer: ServiceCustomerRef
        invoiced: string
        coveredParts: string
        margin: string
        /** "38.2"; null when nothing is invoiced. */
        marginPercent: string | null
      }[]
    | null
}
